// Date#toLocaleDateString / toLocaleTimeString / toLocaleString build a brand-new
// Intl.DateTimeFormat on every call: ~0.1 ms on a desktop, several times that on a phone, and
// lists call them for every row and calendar cell. These versions give the same results but
// reuse one formatter per (locales, options, device zone). Installed once at startup.

import { deviceZone } from './time';

type Opts = Record<string, unknown>;
type Required = 'date' | 'time' | 'any';

const DATE_FIELDS = ['weekday', 'year', 'month', 'day'];
const TIME_FIELDS = ['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits'];

/** The spec's defaults: no date/time fields asked for → numeric date and/or time. */
function withDefaults(opts: Opts, required: Required): Opts {
  const needs =
    (required === 'time' || !DATE_FIELDS.some((k) => opts[k] !== undefined)) &&
    (required === 'date' || !TIME_FIELDS.some((k) => opts[k] !== undefined));
  if (!needs) return opts;
  const o = { ...opts };
  if (required !== 'time') o.year = o.month = o.day = 'numeric';
  if (required !== 'date') o.hour = o.minute = o.second = 'numeric';
  return o;
}

const cache = new Map<string, Intl.DateTimeFormat>();

function formatter(locales: unknown, options: unknown, required: Required) {
  const okLocales =
    locales === undefined ||
    typeof locales === 'string' ||
    (Array.isArray(locales) && locales.every((l) => typeof l === 'string'));
  if (!okLocales) return null;
  if (options !== undefined && (options === null || typeof options !== 'object')) return null;
  const opts = (options ?? {}) as Opts;
  // dateStyle/timeStyle have their own rules (and errors): leave those to the browser.
  if (opts.dateStyle !== undefined || opts.timeStyle !== undefined) return null;
  // Formatters without a timeZone capture the device's: key on it so travel still works.
  const key = JSON.stringify([locales ?? null, opts, required, deviceZone()]);
  let f = cache.get(key);
  if (!f) {
    if (cache.size > 300) cache.clear();
    f = new Intl.DateTimeFormat(
      locales as string | string[] | undefined,
      withDefaults(opts, required),
    );
    cache.set(key, f);
  }
  return f;
}

function install(
  name: 'toLocaleDateString' | 'toLocaleTimeString' | 'toLocaleString',
  required: Required,
) {
  const original = Date.prototype[name];
  Date.prototype[name] = function (this: Date, locales?: unknown, options?: unknown) {
    if (!Number.isNaN(this.getTime())) {
      try {
        const f = formatter(locales, options, required);
        if (f) return f.format(this);
      } catch {
        // Bad locale or option: let the original throw (or cope) the usual way.
      }
    }
    return original.call(this, locales as never, options as never);
  } as Date[typeof name];
}

let installed = false;
export function installIntlCache() {
  if (installed) return;
  installed = true;
  install('toLocaleDateString', 'date');
  install('toLocaleTimeString', 'time');
  install('toLocaleString', 'any');
}
